import { shallowRef, markRaw, type ShallowRef, type Ref } from "vue";
import * as Cesium from "cesium";
import type { SelectedAsset } from "@/stores/detailPanel";

interface UseAssetTilesetsOptions {
    viewer: ShallowRef<Cesium.Viewer | null>;
    currentTileset?: ShallowRef<Cesium.Cesium3DTileset | null>;
    resetHighlight?: () => void;
    clearSelection?: () => void;
    treeTilesetPath: string;
    firehydrantTilesetPath: string;
    poleTilesetPath: string;
    selectedAsset: Ref<SelectedAsset | null>;
}

export function useAssetTilesets({
    viewer,
    currentTileset,
    resetHighlight,
    clearSelection,
    treeTilesetPath,
    firehydrantTilesetPath,
    poleTilesetPath,
    selectedAsset,
}: UseAssetTilesetsOptions) {
    const treeTileset = shallowRef<Cesium.Cesium3DTileset | null>(null);
    const firehydrantTileset = shallowRef<Cesium.Cesium3DTileset | null>(null);
    const poleTileset = shallowRef<Cesium.Cesium3DTileset | null>(null);
    const buildingsTileset = shallowRef<Cesium.Cesium3DTileset | null>(null);

    function makeTilesetToggler(
        tilesetRef: ShallowRef<Cesium.Cesium3DTileset | null>,
        tilesetPath: string
    ) {
        return async () => {
            const v = viewer.value;
            if (!v) return;

            if (tilesetRef.value) {
                if (selectedAsset.value?.tileset === tilesetRef.value) {
                    resetHighlight?.();
                    clearSelection?.();
                }

                v.scene.primitives.remove(tilesetRef.value);
                tilesetRef.value = null;
                v.scene.requestRender();
                return;
            }

            const tileset = markRaw(
                await Cesium.Cesium3DTileset.fromUrl(tilesetPath)
            );
            tilesetRef.value = v.scene.primitives.add(tileset) as Cesium.Cesium3DTileset;
            v.scene.requestRender();
        };
    }

    const toggleTrees = makeTilesetToggler(treeTileset, treeTilesetPath);
    const toggleFirehydrants = makeTilesetToggler(
        firehydrantTileset,
        firehydrantTilesetPath
    );
    const togglePoles = makeTilesetToggler(poleTileset, poleTilesetPath);

    const toggleBuildingAsset = async () => {
        const v = viewer.value;
        if (!v) return;

        if (buildingsTileset.value) {
            if (selectedAsset.value?.tileset === buildingsTileset.value) {
                resetHighlight?.();
                clearSelection?.();
            }

            v.scene.primitives.remove(buildingsTileset.value);
            buildingsTileset.value = null;
            v.scene.requestRender();
            return;
        } else {
            try {
                const tileset = v.scene.primitives.add(
                    await Cesium.Cesium3DTileset.fromIonAssetId(4170615),
                );

                buildingsTileset.value = tileset;
                v.scene.primitives.add(tileset);
                v.scene.requestRender();
                console.log("Building tileset loaded");
            } catch (error) {
                console.error("Error loading tileset:", error);
            }
        }
    };

    return {
        treeTileset,
        firehydrantTileset,
        poleTileset,
        buildingsTileset,
        toggleTrees,
        toggleFirehydrants,
        togglePoles,
        toggleBuildingAsset,
    };
}
